import { Query, skipToken, useQuery } from "@tanstack/react-query";

import { getRanaApiV1 } from "@nens/rana/api-v1";
import { ApiError, OurUseQueryOptions } from "@nens/react-query-utils";
import {
  PaginatedThreediModelTaskList,
  ThreediModelTask,
  getHCCApi,
} from "@nens/threedi/openapi-client";

import { useInvalidateIfOutdatedHccGridadminQuery } from "./query-hcc-grid-admin";
import { useInvalidateIfOutdatedHccSimulationTemplatesQuery } from "./query-hcc-simulation-templates";

const MODEL_TASKS_POLL_INTERVAL = 3000;

function taskIsFinished(task: ThreediModelTask): boolean {
  return task.status === "success" || task.status === "failure" || task.status === "revoked";
}

export const getHccModelTasksQueryKey = (threediModelId: number | undefined) =>
  ["hcc-model-tasks", threediModelId] as const;

const getHccModelTasksQuery = (
  threediModelId: number | undefined,
  invalidateIfOutdated: (finishedAt: Date) => void
): OurUseQueryOptions<PaginatedThreediModelTaskList> => {
  return getRanaApiV1().getQueryOptions({
    queryKey: getHccModelTasksQueryKey(threediModelId),
    queryFn:
      threediModelId !== undefined
        ? async () => {
            const response = await getHCCApi().threedimodelsTasksList({
              threedimodelPk: threediModelId,
            });

            // Once tasks finish, their lastUpdated marks when the produced files (gridadmin, simulation templates) became available.
            const latestTaskUpdatedAt = response.results.reduce<Date | undefined>(
              (latest, task) =>
                task.lastUpdated && (!latest || task.lastUpdated > latest)
                  ? task.lastUpdated
                  : latest,
              undefined
            );

            // Only invalidate once all tasks are done, and only if not already refetched since then, to avoid needless refetches on every poll.
            if (
              response.results.length > 0 &&
              response.results.every(taskIsFinished) &&
              latestTaskUpdatedAt
            ) {
              invalidateIfOutdated(latestTaskUpdatedAt);
            }

            return response;
          }
        : skipToken,
    refetchInterval: (
      query: Query<PaginatedThreediModelTaskList, ApiError, PaginatedThreediModelTaskList>
    ) => {
      // Once finished, tasks for this model id won't change again, so stop polling for good.
      const currentData = query.state.data;
      if (currentData === undefined) return MODEL_TASKS_POLL_INTERVAL;
      return currentData.results.every(taskIsFinished) ? false : MODEL_TASKS_POLL_INTERVAL;
    },
  });
};

export const useHccModelTasksQuery = (threediModelId: number | undefined) => {
  const invalidateSimulationTemplateIfOutdated =
    useInvalidateIfOutdatedHccSimulationTemplatesQuery(threediModelId);
  const invalidateIfOutdatedHccGridadminQuery =
    useInvalidateIfOutdatedHccGridadminQuery(threediModelId);

  // As soon as all tasks are finished, invalidate the queries for the produced files (gridadmin, simulation templates) if they are stale.
  // See "if-outdated.ts" for more details on how this works.
  const invalidateIfOutdated = (finishedAt: Date) => {
    invalidateIfOutdatedHccGridadminQuery(finishedAt);
    invalidateSimulationTemplateIfOutdated(finishedAt);
  };

  return useQuery(getHccModelTasksQuery(threediModelId, invalidateIfOutdated));
};
